import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { getSortedCountries } from '@/constants/countries';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from '@/lib/queryClient';
import { useToast } from "@/hooks/use-toast";
import { Plus, Upload, X } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';
import { CreateOrganizationModal } from "@/components/CreateOrganizationModal";
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { PermissionButton } from "@/components/PermissionButton";
import { LogoCropModal } from '@/components/LogoCropModal';
import { useOrganizationStore } from '@/stores/organizationStore';

type OrgInfo = {
  id: string;
  name?: string;
  website?: string;
  industry?: string;
  size?: string;
  address?: string; // Legacy single address field (deprecated)
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  contactEmail?: string;
  contactPhone?: string;
  language?: string;
  currency?: string;
  logoUrl?: string;
};

export function OrganizationTab() {
  const { t } = useTranslation(['pages.settings']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { activeOrganizationId, setSelectedOrgId } = useOrganizationSelector();
  const selectedOrganizationId = useOrganizationStore((s) => s.selectedOrganizationId);
  const selectedOrganizationName = useOrganizationStore((s) => s.selectedOrganizationName);
  const setStoredOrganization = useOrganizationStore((s) => s.setOrganization);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: '',
    contactEmail: '',
    contactPhone: '',
    logoUrl: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [showLogoCropModal, setShowLogoCropModal] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [orgLanguage, setOrgLanguage] = useState<string>('en');
  const [savingLang, setSavingLang] = useState(false);
  const [orgCurrency, setOrgCurrency] = useState<string>('EUR');
  const [savingCurrency, setSavingCurrency] = useState(false);

  // Fetch organization data
  const { data: orgMeResponse, isLoading: orgLoading } = useQuery({
    queryKey: ["/api/orgs/me", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId 
        ? `/api/orgs/me?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : '/api/orgs/me';
      const res = await apiRequest(url);
      return await res.json();
    },
    enabled: true,
    staleTime: 60_000,
    retry: false,
  });
  const orgInfo: OrgInfo | null = (orgMeResponse as any)?.data || null;
  const orgId = orgInfo?.id || null;

  const syncOrganizationCaches = (patch: Partial<OrgInfo> & { language?: string; currency?: string }) => {
    if (!orgId) return;

    queryClient.setQueriesData({ queryKey: ["/api/orgs/me"] }, (current: any) => {
      if (!current?.data || current.data.id !== orgId) {
        return current;
      }

      return {
        ...current,
        data: {
          ...current.data,
          ...patch,
        },
      };
    });

    if (patch.name) {
      queryClient.setQueriesData({ queryKey: ["/api/orgs/my-organizations"] }, (current: any) => {
        if (!Array.isArray(current?.data)) {
          return current;
        }

        return {
          ...current,
          data: current.data.map((organization: any) =>
            organization.id === orgId
              ? { ...organization, name: patch.name }
              : organization
          ),
        };
      });

      queryClient.setQueriesData({ queryKey: ['auth', 'me'] }, (current: any) => {
        if (!current?.user || current.user.organizationId !== orgId) {
          return current;
        }

        return {
          ...current,
          user: {
            ...current.user,
            organizationName: patch.name,
          },
        };
      });

      if (selectedOrganizationId === orgId && patch.name !== selectedOrganizationName) {
        setStoredOrganization(orgId, patch.name);
      }
    }
  };

  // Initialize form data when orgInfo loads
  useEffect(() => {
    if (orgInfo) {
      setFormData({
        name: orgInfo.name || '',
        website: orgInfo.website || '',
        industry: orgInfo.industry || '',
        size: orgInfo.size || '',
        street: orgInfo.street || '',
        houseNumber: orgInfo.houseNumber || '',
        postalCode: orgInfo.postalCode || '',
        city: orgInfo.city || '',
        country: orgInfo.country || '',
        contactEmail: orgInfo.contactEmail || '',
        contactPhone: orgInfo.contactPhone || '',
        logoUrl: orgInfo.logoUrl || '',
      });
      if (orgInfo.language) {
        setOrgLanguage(orgInfo.language);
      }
      if (orgInfo.currency) {
        setOrgCurrency(orgInfo.currency);
      }
    }
  }, [orgInfo]);

  // Update organization mutation
  const updateOrgMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!orgId) throw new Error('No organization ID');
      const res = await apiRequest(`/api/orgs/${orgId}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: data.name,
          website: data.website || null,
          industry: data.industry || null,
          size: data.size || null,
          street: data.street || null,
          houseNumber: data.houseNumber || null,
          postalCode: data.postalCode || null,
          city: data.city || null,
          country: data.country || null,
          contact_email: data.contactEmail || null,
          contact_phone: data.contactPhone || null,
          logo_url: data.logoUrl || null,
        }),
      });
      return await res.json();
    },
    onSuccess: async (_response, variables) => {
      syncOrganizationCaches({
        name: variables.name,
        website: variables.website || null,
        industry: variables.industry || null,
        size: variables.size || null,
        street: variables.street || null,
        houseNumber: variables.houseNumber || null,
        postalCode: variables.postalCode || null,
        city: variables.city || null,
        country: variables.country || null,
        contactEmail: variables.contactEmail || null,
        contactPhone: variables.contactPhone || null,
        logoUrl: variables.logoUrl || null,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/orgs/me"] }),
        queryClient.invalidateQueries({ queryKey: ['/api/orgs/my-organizations'] }),
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
      ]);
      toast({
        title: t('pages.settings:organization.saveSuccess'),
        description: t('pages.settings:organization.saveSuccessDesc'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('pages.settings:organization.saveError'),
        description: error?.message || t('pages.settings:organization.saveErrorDesc'),
        variant: 'destructive',
      });
    },
  });

  // Logo upload mutation (after crop)
  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);
      const res = await apiRequest('/api/uploads/organization-logo', {
        method: 'POST',
        body: formDataUpload,
      });
      return await res.json();
    },
    onSuccess: (data) => {
      if (data.success && data.data?.url) {
        const updatedFormData = { ...formData, logoUrl: data.data.url };
        setFormData(updatedFormData);
        // Also update the organization immediately
        if (orgId) {
          updateOrgMutation.mutate(updatedFormData);
        }
        toast({
          title: t('pages.settings:organization.logoUploadSuccess'),
          description: t('pages.settings:organization.logoUploadSuccessDesc'),
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: t('pages.settings:organization.logoUploadError'),
        description: error?.message || t('pages.settings:organization.logoUploadErrorDesc'),
        variant: 'destructive',
      });
    },
    onSettled: () => setIsUploadingLogo(false),
  });

  const handleSave = async () => {
    if (!orgId) return;
    setIsSaving(true);
    try {
      await updateOrgMutation.mutateAsync(formData);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle logo file selection (before crop)
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: t('pages.settings:organization.logoUploadError'),
        description: t('pages.settings:organization.logoUploadInvalidType'),
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t('pages.settings:organization.logoUploadError'),
        description: t('pages.settings:organization.logoUploadTooLarge'),
        variant: 'destructive',
      });
      return;
    }

    // Open crop modal
    setSelectedLogoFile(file);
    setShowLogoCropModal(true);
    
    // Reset file input
    e.target.value = '';
  };

  const handleLogoCropSave = (croppedFile: File) => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
    setIsUploadingLogo(true);
    uploadLogoMutation.mutate(croppedFile);
  };

  const handleLogoCropCancel = () => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
  };

  const handleRemoveLogo = () => {
    setFormData({ ...formData, logoUrl: '' });
    if (orgId) {
      updateOrgMutation.mutate({ ...formData, logoUrl: '' });
    }
  };

  const handleCreateSuccess = (newOrgId: string) => {
    setShowCreateModal(false);
    // Switch to the new organization
    setSelectedOrgId(newOrgId);
    // Invalidate queries to refresh data
    queryClient.invalidateQueries({ queryKey: ["/api/orgs/me"] });
    queryClient.invalidateQueries({ queryKey: ["/api/orgs/my-organizations"] });
    queryClient.invalidateQueries({ queryKey: ['/api/orgs/check-ownership'] });
    queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
  };

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LogoLoader size="sm" />
      </div>
    );
  }

  // Show create organization option only when no selected organization context exists.
  if (!orgInfo) {
    return (
      <div>
        <Card className="settings-card-muted">
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('pages.settings:organization.noOrganization.title')}
              </h3>
              <p className="text-sm text-slate-600">
                {t('pages.settings:organization.noOrganization.description')}
              </p>
              <Button onClick={() => setShowCreateModal(true)} variant="pillOutline" className="bg-white hover:bg-white">
                <Plus className="w-4 h-4 mr-2" />
                {t('pages.settings:organization.createButton')}
              </Button>
            </div>
          </CardContent>
        </Card>
        <CreateOrganizationModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          onSuccess={handleCreateSuccess}
        />
      </div>
    );
  }

  // Show organization profile form
  return (
    <div>
      <div className="space-y-3">
        <Card className="settings-card-muted">
          <CardHeader>
            <CardTitle className="text-xs">{t('pages.settings:organization.profile.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Logo Upload Section */}
            <div className="space-y-2">
              <div className="text-xs">{t('pages.settings:organization.profile.logo')}</div>
              <div className="flex items-center gap-3">
                {formData.logoUrl ? (
                  <div className="relative">
                    <img
                      src={formData.logoUrl}
                      alt="Organization logo"
                      className="h-20 w-20 object-contain border border-slate-200 rounded-lg p-2 bg-white"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white"
                      onClick={handleRemoveLogo}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50">
                    <Upload className="h-6 w-6 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={isUploadingLogo}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload">
                    <Button
                      type="button"
                      variant="pillOutline"
                      size="sm"
                      className="bg-white hover:bg-white"
                      disabled={isUploadingLogo}
                      asChild
                    >
                      <span>
                        {isUploadingLogo ? (
                          <>
                            <LogoLoader size="sm" />
                            {t('pages.settings:organization.logoUploading')}
                          </>
                        ) : (
                          <>
                            <Upload className="w-3 h-3 mr-2" />
                            {formData.logoUrl ? t('pages.settings:organization.logoChange') : t('pages.settings:organization.logoUpload')}
                          </>
                        )}
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    {t('pages.settings:organization.logoHint')}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CornerLabelField label={t('pages.settings:organization.profile.name')}>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('pages.settings:organization.profile.namePlaceholder')}
                />
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:organization.profile.website')}>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder={t('pages.settings:organization.profile.websitePlaceholder')}
                />
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:organization.profile.industry')}>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder={t('pages.settings:organization.profile.industryPlaceholder')}
                />
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:organization.profile.size')}>
                <Select
                  value={formData.size}
                  onValueChange={(value) => setFormData({ ...formData, size: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.settings:organization.profile.sizePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">{t('pages.settings:organization.profile.sizeOptions.small')}</SelectItem>
                    <SelectItem value="medium">{t('pages.settings:organization.profile.sizeOptions.medium')}</SelectItem>
                    <SelectItem value="large">{t('pages.settings:organization.profile.sizeOptions.large')}</SelectItem>
                    <SelectItem value="enterprise">{t('pages.settings:organization.profile.sizeOptions.enterprise')}</SelectItem>
                  </SelectContent>
                </Select>
              </CornerLabelField>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.street')}>
                  <Input
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder={t('pages.settings:sections.profile.fields.streetPlaceholder') as string}
                  />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.houseNumber')}>
                  <Input
                    value={formData.houseNumber}
                    onChange={(e) => setFormData({ ...formData, houseNumber: e.target.value })}
                    placeholder={t('pages.settings:sections.profile.fields.houseNumberPlaceholder') as string}
                  />
                </CornerLabelField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.postalCode')}>
                  <Input
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder={t('pages.settings:sections.profile.fields.postalCodePlaceholder') as string}
                  />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.city')}>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder={t('pages.settings:sections.profile.fields.cityPlaceholder') as string}
                  />
                </CornerLabelField>
              </div>
              <CornerLabelField label={t('pages.settings:sections.profile.fields.country')}>
                <Select
                  value={formData.country || ''}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.settings:sections.profile.fields.countryPlaceholder') as string} />
                  </SelectTrigger>
                  <SelectContent>
                    {getSortedCountries(i18n.language as 'en' | 'de' | 'tr').map((country) => (
                      <SelectItem key={country.code} value={country.code} className="text-xs">
                        {country[i18n.language as 'en' | 'de' | 'tr']}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:organization.profile.contactEmail')}>
                <Input
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  placeholder={t('pages.settings:organization.profile.contactEmailPlaceholder')}
                />
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:organization.profile.contactPhone')}>
                <Input
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                  placeholder={t('pages.settings:organization.profile.contactPhonePlaceholder')}
                />
              </CornerLabelField>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave}
                disabled={isSaving || !formData.name.trim()}
                size="sm"
                variant="pillOutline"
                className="bg-white hover:bg-white"
              >
                {isSaving ? (
                  <>
                    <LogoLoader size="sm" />
                    {t('pages.settings:organization.saving')}
                  </>
                ) : (
                  t('pages.settings:organization.save')
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Organization language */}
        <Card className="settings-card-muted">
          <CardHeader>
            <CardTitle className="text-xs">{t('pages.settings:organization.language.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-48">
                <Select value={orgLanguage} onValueChange={(v) => setOrgLanguage(v)}>
                  <SelectTrigger><SelectValue placeholder={t('pages.settings:organization.language.placeholder')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="tr">Türkçe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <PermissionButton
                size="sm"
                className="bg-white hover:bg-white"
                variant="pillOutline"
                permission="manage_organization"
                fallbackText="You don't have permission to update organization"
                disabled={!orgId || savingLang}
                onClick={async () => {
                  if (!orgId) return;
                  try {
                    setSavingLang(true);
                    await apiRequest(`/api/orgs/${orgId}`, {
                      method: 'PUT',
                      body: JSON.stringify({ language: orgLanguage })
                    });
                    syncOrganizationCaches({ language: orgLanguage });
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["/api/orgs/me"] }),
                      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
                    ]);
                    toast({
                      title: t('pages.settings:organization.saveSuccess'),
                      description: t('pages.settings:organization.saveSuccessDesc'),
                    });
                  } catch (error: any) {
                    toast({
                      title: t('pages.settings:organization.saveError'),
                      description: error?.message || t('pages.settings:organization.saveErrorDesc'),
                      variant: 'destructive',
                    });
                  } finally {
                    setSavingLang(false);
                  }
                }}
              >
                {savingLang ? t('pages.settings:organization.language.saving') : t('pages.settings:organization.language.save')}
              </PermissionButton>
            </div>
            <p className="text-[11px] text-slate-500">{t('pages.settings:organization.language.description')}</p>
          </CardContent>
        </Card>

        {/* Organization currency */}
        <Card className="settings-card-muted">
          <CardHeader>
            <CardTitle className="text-xs">{t('pages.settings:organization.currency.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-48">
                <Select value={orgCurrency} onValueChange={(v) => setOrgCurrency(v)}>
                  <SelectTrigger><SelectValue placeholder={t('pages.settings:organization.currency.placeholder')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="GBP">GBP (£)</SelectItem>
                    <SelectItem value="CHF">CHF (CHF)</SelectItem>
                    <SelectItem value="TRY">TRY (₺)</SelectItem>
                    <SelectItem value="PLN">PLN (zł)</SelectItem>
                    <SelectItem value="DKK">DKK (kr)</SelectItem>
                    <SelectItem value="SEK">SEK (kr)</SelectItem>
                    <SelectItem value="NOK">NOK (kr)</SelectItem>
                    <SelectItem value="CZK">CZK (Kč)</SelectItem>
                    <SelectItem value="HUF">HUF (Ft)</SelectItem>
                    <SelectItem value="RON">RON (lei)</SelectItem>
                    <SelectItem value="BGN">BGN (лв)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <PermissionButton
                size="sm"
                className="bg-white hover:bg-white"
                variant="pillOutline"
                permission="manage_organization"
                fallbackText="You don't have permission to update organization"
                disabled={!orgId || savingCurrency}
                onClick={async () => {
                  if (!orgId) return;
                  try {
                    setSavingCurrency(true);
                    await apiRequest(`/api/orgs/${orgId}`, {
                      method: 'PUT',
                      body: JSON.stringify({ currency: orgCurrency })
                    });
                    syncOrganizationCaches({ currency: orgCurrency });
                    await Promise.all([
                      queryClient.invalidateQueries({ queryKey: ["/api/orgs/me"] }),
                      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
                    ]);
                    toast({
                      title: t('pages.settings:organization.saveSuccess'),
                      description: t('pages.settings:organization.saveSuccessDesc'),
                    });
                  } catch (error: any) {
                    toast({
                      title: t('pages.settings:organization.saveError'),
                      description: error?.message || t('pages.settings:organization.saveErrorDesc'),
                      variant: 'destructive',
                    });
                  } finally {
                    setSavingCurrency(false);
                  }
                }}
              >
                {savingCurrency ? t('pages.settings:organization.currency.saving') : t('pages.settings:organization.currency.save')}
              </PermissionButton>
            </div>
            <p className="text-[11px] text-slate-500">{t('pages.settings:organization.currency.description')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Logo Crop Modal */}
      <LogoCropModal
        isOpen={showLogoCropModal}
        imageFile={selectedLogoFile}
        onClose={handleLogoCropCancel}
        onSave={handleLogoCropSave}
        title={t('pages.settings:organization.logoCropTitle') || 'Crop Logo'}
        instructions={t('pages.settings:organization.logoCropInstructions') || 'Drag to position and use zoom controls to adjust your logo. The circular area will be your logo.'}
      />
    </div>
  );
}

export default OrganizationTab;
